import React, { Fragment } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSave,
    faFileInvoiceDollar,
    faCheckCircle,
} from '@fortawesome/free-solid-svg-icons';
import { Button, FormGroup, Input } from 'reactstrap';
import { isEqual } from 'lodash-es';
import { LinearProgress } from '@mui/material';
import Select from 'react-select';
import { Prompt, withRouter } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    ChargeType,
    ChargeTypeConfiguration,
    ChargeTypeViewModel
} from './ChargeType';
import ChargeTypeConfigurationForm from './ChargeTypeConfigurationForm';
import CommonContext, { ApiRoutes, AppNavPaths } from '../Common';
import {
    AppPageForm,
    FlexCenterRow,
    FormCheckbox,
    FormGroupColumn,
    FormLabel,
    GroupedRow,
    onFieldChange,
    onReactSelectChanged,
    SubHeading,
    ToastMessage,
    ValidationErrorMessage,
} from '../common/forms/FormElements';
import { util } from '../Util';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import { handleFormSaveError } from '../common/forms/ValidationError';

class ChargeTypeForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();
        this.minPayRef = React.createRef();
        this.maxPayRef = React.createRef();
        this.exceptionFormRef = React.createRef();
        this.rateFormRef = React.createRef();
        this.rateHistoryFormRef = React.createRef();
        this.state = new ChargeTypeViewModel();
        this.handlers.change = this.handlers.change.bind(this);
        this.handlers.select = this.handlers.select.bind(this);
        this.handlers.baseConfig.canBreak = this.handlers.baseConfig.canBreak.bind(this);
        this.handlers.selectedException.canBreak = this.handlers.selectedException.canBreak.bind(this);
    }

    componentDidMount() { return this.populateState(); }

    componentDidUpdate(prevProps, prevState) {
        if (
            prevProps
            && this.props.match.params.id !== (prevProps.match.params ?? {}).id
        ) {
            this.populateState();
        }
    }

    onAddException = () => {
        this.exceptionFormRef.current.resetForm(); // clear validation
        this.setState((state) => {
            state.selectedException = {
                ...new ChargeTypeConfiguration(),
                chargeTypeId: this.state.chargeType.id ?? null,
                chargeTypeConfigurationTypeId: 2,
            };
            state.showExceptionForm = true;
            return state;
        });
        this.context.setFormOpened(true); // lock all other page fields while form is open
    };

    onClearErrors = () => this.setState((state) => ({ errors: {} }));

    onEditException = (exception) => {
        const cloned = util.object.clone(exception);
        this.exceptionFormRef.current.resetForm();
        this.setState({ selectedException: cloned, showExceptionForm: true });
        this.context.setFormOpened(true);
    };

    onExceptionFormClosed = () => this.setState({ showExceptionForm: false });

    onExceptionSaved = () => {
        const { selectedException, chargeType } = this.state;

        if (chargeType.unitsId == 2) {
            selectedException.paidBreak = null;
            selectedException.canBreak = null;
            selectedException.breakDescription = null;
            selectedException.minimumPayUnits = null;
            selectedException.maximumPayUnits = null;
        }

        if (selectedException.doesNotApply) {
            selectedException.increment = null;
            selectedException.defaultValue = null;
            selectedException.paidBreak = null;
            selectedException.canBreak = null;
            selectedException.breakDescription = null;
            selectedException.minimumPayUnits = null;
            selectedException.maximumPayUnits = null;
        }

        if (!selectedException.breakHoursExceededThreshold) {
            selectedException.breakHoursExceededThreshold = 0;
        }

        chargeType.exceptions = util.array.upsert(
            selectedException,
            chargeType.exceptions,
            'id',
        );
        this.exceptionFormRef.current.resetForm();
        this.setState({
            chargeType,
            showExceptionForm: false,
        });
    };

    onSubmit = (e) => {
        this.onClearErrors();
        this.setSaving(true);

        if (this.state.chargeType.baseConfig.isPermit && !this.state.chargeType.baseConfig.isSurcharge) {
            toast.error('Permit Fees must be configured as a surcharge.');
            this.setSaving(false);
            return;
        }

        // if (!this.additionalValidation()) {
        //    this.setSaving(false);
        //    return false;
        // }

        const cloned = util.object.clone(this.state.chargeType);

        /** Revert temporary hashes used in row keys.  Need this to get around
            fluent API.
        */
        if (cloned.baseConfig.id.constructor === String) cloned.baseConfig.id = null;

        for (const exception of cloned.exceptions) {
            exception.id = exception.id.constructor === String ? null : exception.id;
        }

        this.props.location.pathname == AppNavPaths.ChargeTypeNew
            ? this.create(cloned)
            : this.update(cloned);
    };

    setSaving = (b) => this.setState({ saving: b });

    additionalValidation = () => {
        const { chargeType } = this.state;
        let valid = true;

        if (
            (chargeType.baseConfig.maximumPayUnits ?? 0)
            < (chargeType.baseConfig.minimumPayUnits ?? 0)
        ) {
            document
                .getElementsByName(this.minPayRef.current.props.name)[0]
                .setCustomValidity('Invalid field.');
            document
                .getElementsByName(this.maxPayRef.current.props.name)[0]
                .setCustomValidity('Invalid field.');
            valid = false;
        }
        return valid;
    };

    create = async (chargeType) => {
        chargeType.id = null;

        const response = await util.fetch
            .post(ApiRoutes.chargeType.create(), chargeType)
            .catch(this.handleSaveError);

        if (response && !isNaN(response)) {
            toast.success(
                <ToastMessage
                    icon={faCheckCircle}
                    header="Save Successful"
                    message="Saved."
                />,
            );
            util.navigation.localRedirect(
                this,
                `${AppNavPaths.ChargeType}/${response}`,
            );
        }

        this.setSaving(false);
    };

    handleSaveError = (err) => handleFormSaveError(this, err);

    handlers = {
        baseConfig: {
            canBreak: (e) => {
                const { checked } = e.target;
                this.setState(
                    (state) => (
                        (state.chargeType.baseConfig = this.processCanBreak(
                            state.chargeType.baseConfig,
                            checked,
                        )),
                        state
                    ),
                );
            },
        },
        selectedException: {
            canBreak: (e) => {
                const { checked } = e.target;
                this.setState(
                    (state) => (
                        (state.selectedException = this.processCanBreak(
                            state.selectedException,
                            checked,
                        )),
                        state
                    ),
                );
            },
            DNA: (e) => {
                const { checked } = e.target;
                const selectedException = { ...this.state.selectedException };
                if (!checked) {
                    selectedException.increment = '';
                    selectedException.canBreak = false;
                    selectedException.paidBreak = false;
                    selectedException.breakDescription = '';
                    selectedException.minimumPayUnits = '';
                    selectedException.maximumPayUnits = '';
                }
                selectedException.doesNotApply = checked;
                this.setState({ selectedException });
            },
        },
        change: onFieldChange,
        select: onReactSelectChanged,
        onChargeUnitsChanged: (s, e) => {
            // baseconfig only
            const { chargeType } = this.state;
            const val = (s ?? {}).value;
            chargeType.unitsId = val;
            // if flat clear out non flat fields and set to 1
            if (!!val && val == 2) {
                chargeType.baseConfig.minimumPayUnits = '';
                chargeType.baseConfig.maximumPayUnits = '';
                chargeType.baseConfig.breakDescription = '';
                chargeType.baseConfig.canBreak = false;
                chargeType.baseConfig.paidBreak = false;
            }

            chargeType.baseConfig.increment = '';
            chargeType.baseConfig.defaultValue = '';

            this.setState({
                chargeType,
            });
        },
    };

    hasHourlyEquipment = () => {
        const { chargeType } = this.state;

        if (chargeType.resourceTypeId === 2 && chargeType.unitsId === 1) {
            return true;
        } 

        return false;
    };

    async populateState() {
        const { id } = this.props.match.params;

        var setupSurcharges = [];
        let [
            resGroups,
            groups,
            units,
            statuses,
            equipmentTypes,
            customers,
            chargeType,
            costCodes,
            departments,
            linkedChargeTypes
        ] = await Promise.all([
            util.fetch.js(ApiRoutes.userGroup.all()),
            util.fetch.js(ApiRoutes.auth.groups()),
            util.fetch.js(ApiRoutes.chargeTypeUnit.all()),
            util.fetch.js(ApiRoutes.chargeTypeStatus.all()),
            util.fetch.js(ApiRoutes.typeAheads.equipmentTypes()),
            util.fetch.js(ApiRoutes.typeAheads.customers()),
            id
                ? util.fetch.js(ApiRoutes.chargeType.byId(id))
                : new ChargeType(),
            util.fetch.js(ApiRoutes.typeAheads.costCodes()),
            util.fetch.js(ApiRoutes.typeAheads.departments()),
            util.fetch.js(ApiRoutes.typeAheads.employeeChargeTypes())
        ]);

        const originalData = util.object.clone(chargeType);

        var acctTypes = [
            { label: 'Specific Job ', value: 1 },
            { label: 'Truck\'s Job ', value: 2 },
            { label: 'Truck\'s Job (Labor Only)', value: 5 },
            { label: 'Department Only ', value: 3 },
            { label: 'Department Equipment ', value: 4 }
        ]

        this.setState((state) => ({
            originalData,
            chargeType,
            customers,
            costCodes,
            acctTypes,
            departments,
            loading: false,
            setupSurcharges: setupSurcharges.map((x) => ({ label: x.description, value: x.id })),
            resGroups: (resGroups = resGroups.map((x) => ({ label: x.groupName, value: x.id }))),
            groups: groups.map((x) => ({ label: x.groupName, value: x.id })),
            units: units.map((x) => ({ label: x.description, value: x.id })),
            equipmentTypes: (equipmentTypes ?? []).map((x) => ({ label: x.description, value: x.id, isAFAD: x.equipmentTypeAFADType.isAFAD })),
            statuses: (statuses ?? []).map((x) => ({ label: x.description, value: x.id })),
            saving: false,
            linkedChargeTypes
        }));
    }

    // If breaks are turned off, we should clear whether or not they are paid,
    // as well as clearing out the description.
    processCanBreak = (entity, checked) => {
        entity.canBreak = checked;
        entity.paidBreak = !checked ? false : entity.paidBreak;
        entity.breakDescription = !checked ? '' : entity.breakDescription;
        entity.breakHoursExceededThreshold = !checked
            ? 0
            : entity.breakHoursExceededThreshold;
        return entity;
    };

    update = async (chargeType) => {
        const response = await util.fetch
            .put(ApiRoutes.chargeType.byId(chargeType.id), chargeType)
            .catch(this.handleSaveError);

        if (response && parseInt(response.id ?? 0) >= 0) {
            this.setState({
                chargeType: response,
                originalData: response /* also restore this to reset Prompt */,
                saving: false,
                formValidated: false,
            });

            toast.success(
                <ToastMessage
                    icon={faCheckCircle}
                    header="Save Successful"
                    message="Charge Type saved."
                />,
            );
        }

        this.setSaving(false);
    };

    renderForm() {
    // reducers
        const {
            billableOptions,
            chargeType,
            increments,
            units,
            resTypes,
            acctTypes,
            resGroups,
            costCodes,
            setupSurcharges,
            groups,
            statuses,
            equipmentTypes,
            originalData,
            saving,
            departments,
            linkedChargeTypes
        } = this.state;
        const newChargeType = parseInt(chargeType.id ?? 0) <= 0;

        if (!chargeType) return '';

        const { baseConfig } = chargeType;

        const tenantSettings = this.context?.tenant?.tenantSettings || {};

        const {
            useLimitedApprovals,
            enableAFADChargeTypeGrouping,
        } = tenantSettings;

        const hasHourlyEquipment = this.hasHourlyEquipment();

        return (
            <>
                <Prompt
                    when={!saving && !isEqual(originalData, chargeType)}
                    message="You have unsaved changes, are you sure you want to leave?"
                />
                <AppPageForm
                    formShown={this.context.formIsOpen}
                    formId="chargeTypeForm"
                    formHeadingIcon={faFileInvoiceDollar}
                    formHeading={
                        newChargeType ? 'New Charge Type' : 'Edit Charge Type'
                    }
                    formName="chargeTypeForm"
                    formRef={this.formRef}
                    onSubmit={this.onSubmit}
                    setIsValidated={(value) => {
                        this.setState({ formValidated: value });
                    }}
                    isValidated={this.state.formValidated}
                    saving={this.state.saving}
                    errors={this.state.errors}
                    loading={this.state.loading}
                >
                    <SubHeading first>Details</SubHeading>
                    <GroupedRow>
                        <FormGroupColumn>
                            <FormGroup>
                                <FormLabel
                                    htmlFor="description"
                                    text="Charge Type Description"
                                    required
                                />
                                <Input
                                    id="description"
                                    name="chargeType.description"
                                    value={chargeType.description ?? ''}
                                    onChange={this.handlers.change}
                                    placeholder="Enter description"
                                    type="text"
                                    required
                                />
                                <ValidationErrorMessage>
                  Description is required.
                                </ValidationErrorMessage>
                            </FormGroup>

                            <FormGroup>
                                <FormLabel
                                    htmlFor="isBillable"
                                    text="Billable"
                                    required
                                />
                                <ValidatedSelect
                                    id="isBillable"
                                    name="chargeType.isBillable"
                                    required
                                    options={billableOptions}
                                    value={
                                        billableOptions.find(
                                            (bo) => bo.value
                                                === chargeType.isBillable,
                                        ) ?? ''
                                    }
                                    onChange={this.handlers.select}
                                    validationMessage="Choose whether or not this charge type is billable."
                                />
                            </FormGroup>

                            <FormGroup>
                                <FormLabel
                                    htmlFor="units"
                                    text="Charge Units"
                                    required
                                />
                                <ValidatedSelect
                                    id="units"
                                    name="chargeType.unitsId"
                                    required
                                    isDisabled={
                                        (chargeType.exceptions ?? []).length > 0
                                    }
                                    options={units}
                                    value={
                                        (units ?? []).find(
                                            (s) => s.value === chargeType.unitsId,
                                        ) ?? ''
                                    }
                                    onChange={this.handlers.onChargeUnitsChanged.bind(
                                        this,
                                    )}
                                    validationMessage="A units selection is required."
                                />
                            </FormGroup>

                            <FormGroup>
                                <FormLabel
                                    htmlFor="information"
                                    text="Info Description"
                                    required
                                />
                                <textarea
                                    id="information"
                                    name="chargeType.information"
                                    className="form-control"
                                    value={chargeType.information ?? ''}
                                    onChange={this.handlers.change}
                                    placeholder="Enter text"
                                    type="text"
                                    rows="5"
                                    required
                                />
                                <ValidationErrorMessage>
                  Description is required.
                                </ValidationErrorMessage>
                            </FormGroup>

                            <FormGroup>
                                <FormLabel htmlFor="status" text="Status" />
                                <Select
                                    id="status"
                                    name="chargeType.statusId"
                                    required
                                    options={statuses}
                                    value={
                                        (statuses ?? []).find(
                                            (x) => x.value === chargeType.statusId,
                                        ) ?? ''
                                    }
                                    onChange={this.handlers.select}
                                />
                                <ValidationErrorMessage>
                  At least one equipment type is required.
                                </ValidationErrorMessage>
                            </FormGroup>

                            {(chargeType.unitsId == 1 || chargeType.unitsId == 3) && (
                                <FormGroup>
                                    <FormLabel text="Increment" />
                                    <ValidatedSelect
                                        name="chargeType.baseConfig.increment"
                                        required
                                        options={increments}
                                        value={
                                            (increments ?? []).find(
                                                (i) => parseFloat(
                                                    i.value,
                                                )
                                                    === parseFloat(
                                                        baseConfig.increment
                                                        ?? '0',
                                                    ),
                                            ) ?? ''
                                        }
                                        onChange={
                                            this.handlers.select
                                        }
                                        validationMessage="Increment is required."
                                    />
                                </FormGroup>
                            )}
                            
                        </FormGroupColumn>
                        <FormGroupColumn>
                            <FormGroup>
                                <FormLabel
                                    htmlFor="resType"
                                    text="Resource Type"
                                    required
                                />
                                <ValidatedSelect
                                    id="resType"
                                    name="chargeType.resourceTypeId"
                                    required
                                    options={resTypes}
                                    isDisabled={!newChargeType}
                                    value={
                                        (resTypes ?? []).find(
                                            (x) => x.value
                                                === chargeType.resourceTypeId,
                                        ) ?? ''
                                    }
                                    onChange={this.handlers.select}
                                    validationMessage="A type is required"
                                />
                            </FormGroup>

                            <FormGroup>
                                <FormLabel
                                    htmlFor="acctType"
                                    text="Account Coding Type"
                                    required
                                />
                                <ValidatedSelect
                                    id="acctType"
                                    name="chargeType.accountCodingTypeId"
                                    required
                                    options={acctTypes}
                                    value={
                                        (acctTypes ?? []).find(
                                            (x) => x.value
                                                === chargeType.accountCodingTypeId,
                                        ) ?? ''
                                    }
                                    onChange={this.handlers.select}
                                    validationMessage="An account type is required"
                                />
                            </FormGroup>

                            {chargeType.accountCodingTypeId == 1 &&
                                <FormGroup>
                                    <FormLabel
                                        htmlFor="chargeType.externalId"
                                        text="Job"
                                        required
                                    />
                                    <Input
                                        id="chargeType.externalId"
                                        name="chargeType.externalId"
                                        value={chargeType.externalId}
                                        onChange={
                                            this.handlers.change
                                        }
                                        type="text"
                                        required
                                    />
                                    <ValidationErrorMessage>
                                        Job is required
                                    </ValidationErrorMessage>
                                </FormGroup>
                            }

                            {chargeType.resourceTypeId != null && <hr />}

                            {chargeType.resourceTypeId == 5 && (
                                <>
                                    <FormGroup>
                                        <FormLabel text="Resource Type Options" />

                                        <FormCheckbox
                                            id="chargeType.baseConfig.isPermit"
                                            name="chargeType.baseConfig.isPermit"
                                            checked={
                                                baseConfig.isPermit ?? ''
                                            }
                                            onChange={this.handlers.change}
                                            labelText="Is Permit Fee"
                                        />
                                    </FormGroup>

                                    {!baseConfig.isPermit && !baseConfig.isSurcharge

                                        && (
                                            <FormGroup>
                                                <FormLabel text="Setup Surcharge" />
                                                <Select
                                                    id="chargeType.baseConfig.childChargeTypeId"
                                                    name="chargeType.baseConfig.childChargeTypeId"
                                                    isClearable
                                                    options={setupSurcharges}
                                                    value={
                                                        (setupSurcharges ?? []).find(
                                                            (x) => x.value
                                                            === baseConfig.childChargeTypeId,
                                                        ) ?? ''
                                                    }
                                                    onChange={this.handlers.select}
                                                />

                                            </FormGroup>
                                        )}
                                </>
                            )}

                            {chargeType.resourceTypeId == 2 && (
                                <>
                                    <FormGroup>
                                        <FormCheckbox
                                            id="chargeType.isRepair"
                                            name="chargeType.isRepair"
                                            checked={
                                                chargeType.isRepair ?? ''
                                            }
                                            onChange={this.handlers.change}
                                            labelText="Is Repair"
                                        />
                                    </FormGroup>

                                    {chargeType.isRepair === true && chargeType.accountCodingTypeId !== 3 && chargeType.accountCodingTypeId !== 4 && (
                                        <FormGroup style={{ marginLeft: '30px' }}>
                                            <FormLabel
                                                htmlFor="repairDepartment"
                                                text="Department"
                                                required
                                            />
                                            <ValidatedSelect
                                                id="repairDepartment"
                                                name="chargeType.departmentId"
                                                required
                                                options={departments}
                                                value={
                                                    (departments ?? []).find(
                                                        (x) => x.value
                                                            === chargeType.departmentId,
                                                    ) ?? ''
                                                }
                                                onChange={this.handlers.select}
                                                validationMessage="A department is required"
                                            />
                                        </FormGroup>
                                    )}
                                </>
                            )}

                            {chargeType.accountCodingTypeId == 3 && (
                                <>
                                    <FormGroup>
                                        <FormLabel
                                            htmlFor="department"
                                            text="Department"
                                            required
                                        />
                                        <ValidatedSelect
                                            id="department"
                                            name="chargeType.departmentId"
                                            required
                                            options={departments}
                                            value={
                                                (departments ?? []).find(
                                                    (x) => x.value
                                                        === chargeType.departmentId,
                                                ) ?? ''
                                            }
                                            onChange={this.handlers.select}
                                            validationMessage="A department is required"
                                        />
                                    </FormGroup>

                                    <FormGroup>
                                        <FormCheckbox
                                            id="chargeType.isExempt"
                                            name="chargeType.isExempt"
                                            checked={
                                                chargeType.isExempt ?? ''
                                            }
                                            onChange={this.handlers.change}
                                            labelText="Exempt"
                                        />
                                    </FormGroup>

                                    {chargeType.isExempt === true && (

                                        <FormGroup style={{ marginLeft: '30px' }}>
                                            <FormLabel
                                                htmlFor="chargeType.externalId"
                                                text="Other Type"
                                                required
                                            />
                                            <Input
                                                id="chargeType.externalId"
                                                name="chargeType.externalId"
                                                value={chargeType.externalId}
                                                onChange={
                                                    this.handlers.change
                                                }
                                                type="text"
                                                required
                                            />
                                            <ValidationErrorMessage>
                                                GL Code is required
                                            </ValidationErrorMessage>
                                        </FormGroup>
                                        
                                    )}
                                </>

                                    
        
                                
                            )}

                            {chargeType.accountCodingTypeId == 4 && (
                                <>
                                    <FormGroup>
                                        <FormLabel
                                            htmlFor="department"
                                            text="Department"
                                            required
                                        />
                                        <ValidatedSelect
                                            id="department"
                                            name="chargeType.departmentId"
                                            required
                                            options={departments}
                                            value={
                                                (departments ?? []).find(
                                                    (x) => x.value
                                                        === chargeType.departmentId,
                                                ) ?? ''
                                            }
                                            onChange={this.handlers.select}
                                            validationMessage="A department is required"
                                        />
                                    </FormGroup>
                                </>
                            )}

                            {(chargeType.accountCodingTypeId == 1 || chargeType.accountCodingTypeId == 2 || chargeType.accountCodingTypeId == 5) &&
                                <FormGroup>
                                    <FormLabel
                                        htmlFor="costCode"
                                        text="Cost Code"
                                        required
                                    />
                                    <ValidatedSelect
                                        id="costCode"
                                        name="chargeType.costCodeId"
                                        required
                                        options={costCodes}
                                        value={
                                            (costCodes ?? []).find(
                                                (x) => x.value
                                                    === chargeType.costCodeId,
                                            ) ?? ''
                                        }
                                        onChange={this.handlers.select}
                                        validationMessage="A cost code is required"
                                    />
                                </FormGroup>
                            }

                            {chargeType.resourceTypeId == 1 && (
                                <FormGroup>
                                    <FormLabel
                                        htmlFor="groups"
                                        text="Eligible Resource Groups"
                                        required={
                                            chargeType.resourceTypeId === 1
                                            && (chargeType.groups ?? []).length
                                                === 0
                                        }
                                    />
                                    <Select
                                        id="resGroups"
                                        name="chargeType.resourceGroupId"
                                        isClearable
                                        isDisabled={
                                            (chargeType.groups ?? []).length > 0
                                        }
                                        required={
                                            chargeType.resourceTypeId === 1
                                            && (chargeType.groups ?? []).length
                                                === 0
                                        }
                                        options={resGroups}
                                        value={
                                            (resGroups ?? []).find(
                                                (x) => x.value
                                                    === chargeType.resourceGroupId,
                                            ) ?? ''
                                        }
                                        onChange={this.handlers.select}
                                    />
                                    <ValidationErrorMessage>
                  A group selection is required.
                                    </ValidationErrorMessage>
                                </FormGroup>
                            )}

                            {chargeType.resourceTypeId == 1 && (
                                <FormGroup>
                                    <FormLabel
                                        htmlFor="groups"
                                        text="Eligible Employee Roles"
                                        required={
                                            chargeType.resourceTypeId === 1
                                            && (chargeType.resourceGroupId
                                                === null
                                                || chargeType.resourceGroupId
                                                    === 0
                                                || chargeType.resourceGroupId
                                                    === undefined)
                                        }
                                    />
                                    <Select
                                        id="groups"
                                        name="chargeType.groups"
                                        isDisabled={
                                            chargeType.resourceGroupId
                                                !== null
                                            && chargeType.resourceGroupId !== 0
                                            && chargeType.resourceGroupId
                                                !== undefined
                                        }
                                        isMulti
                                        required={
                                            chargeType.resourceTypeId === 1
                                            && (chargeType.resourceGroupId
                                                === null
                                                || chargeType.resourceGroupId
                                                    === 0
                                                || chargeType.resourceGroupId
                                                    === undefined)
                                        }
                                        options={groups}
                                        value={
                                            (groups ?? []).filter((x) => (
                                                chargeType.groups ?? []
                                            ).includes(x.value)) ?? ''
                                        }
                                        onChange={this.handlers.select}
                                    />
                                    <ValidationErrorMessage>
                  At least one role selection is required.
                                    </ValidationErrorMessage>
                                </FormGroup>
                            )}

                            {(chargeType.resourceTypeId == 2 || chargeType.resourceTypeId == 5) && (
                                <>
                                    <FormGroup>
                                        <FormLabel
                                            htmlFor="equipmentTypes"
                                            text="Eligible Equipment Types"
                                            required={
                                                chargeType.resourceTypeId == 2
                                            }
                                        />
                                        <Select
                                            id="equipment"
                                            name="chargeType.equipment"
                                            isMulti
                                            required={
                                                chargeType.resourceTypeId == 2
                                            }
                                            options={equipmentTypes}
                                            value={
                                                (equipmentTypes ?? []).filter((x) => (
                                                    chargeType.equipment ?? []
                                                ).includes(x.value)) ?? ''
                                            }
                                            onChange={this.handlers.select}
                                        />
                                        <ValidationErrorMessage>
                      At least one equipment type is required.
                                        </ValidationErrorMessage>
                                    </FormGroup>

                                    <FormGroup>
                                        <FormLabel
                                            htmlFor="chargeTypes"
                                            text="Link To Charge Type"
                                        />
                                        <Select
                                            id="chargeTypes"
                                            name="chargeType.linkedChargeTypeId"
                                            options={linkedChargeTypes}
                                            value={
                                                (linkedChargeTypes ?? []).find(
                                                    (x) => x.value
                                                        === chargeType.linkedChargeTypeId,
                                                ) ?? ''
                                            }
                                            onChange={this.handlers.select}
                                        />
                                    </FormGroup>
                                </>
                            )}

                            {enableAFADChargeTypeGrouping === true && this.hasAFADEquipment() && (
                                <FormGroup>
                                    <FormLabel
                                        htmlFor="numAFADsInSet"
                                        text="# of AFADs in a set"
                                        required={
                                            chargeType.resourceTypeId == 2
                                        }
                                    />
                                    <Input
                                        className="ml-3 w-auto"
                                        id="chargeType.baseConfig.numAFADsInSet"
                                        name="chargeType.baseConfig.numAFADsInSet"
                                        value={
                                            baseConfig.numAFADsInSet
                                            ?? 1
                                        }
                                        onChange={
                                            this.handlers.change
                                        }
                                        placeholder="Enter 1 if AFADs are billed individually"
                                        type="number"
                                        min={1}
                                        required={
                                            chargeType.resourceTypeId == 2
                                        }
                                    />
                                    <ValidationErrorMessage>
                  A value of at least 1 must be entered.
                                    </ValidationErrorMessage>
                                </FormGroup>
                            )}
                        </FormGroupColumn>
                    </GroupedRow>

                    {chargeType.resourceTypeId == 1 && useLimitedApprovals && (
                        <>
                            <SubHeading>Timesheet Security Groups</SubHeading>
                            <GroupedRow>
                                <FormGroupColumn>
                                    <FormGroup>
                                        <FormLabel
                                            htmlFor="groups"
                                            text="Approver Groups"
                                        />
                                        <Select
                                            id="approverGroups"
                                            name="chargeType.approveLimitedApprovalGroups"
                                            isMulti
                                            options={groups}
                                            value={
                                                (groups ?? []).filter((x) => (
                                                    chargeType.approveLimitedApprovalGroups
                                                        ?? []
                                                ).includes(x.value)) ?? ''
                                            }
                                            onChange={this.handlers.select}
                                        />
                                    </FormGroup>

                                    <FormGroup>
                                        <FormLabel
                                            htmlFor="groups"
                                            text="Rejection Groups"
                                        />
                                        <Select
                                            id="rejectionGroups"
                                            name="chargeType.rejectionLimitedApprovalGroups"
                                            isMulti
                                            options={groups}
                                            value={
                                                (groups ?? []).filter((x) => (
                                                    chargeType.rejectionLimitedApprovalGroups
                                                        ?? []
                                                ).includes(x.value)) ?? ''
                                            }
                                            onChange={this.handlers.select}
                                        />
                                    </FormGroup>

                                    <FormGroup>
                                        <FormLabel
                                            htmlFor="groups"
                                            text="Editor Groups"
                                        />
                                        <Select
                                            id="rejectionGroups"
                                            name="chargeType.editorLimitedApprovalGroups"
                                            isMulti
                                            options={groups}
                                            value={
                                                (groups ?? []).filter((x) => (
                                                    chargeType.editorLimitedApprovalGroups
                                                        ?? []
                                                ).includes(x.value)) ?? ''
                                            }
                                            onChange={this.handlers.select}
                                        />
                                    </FormGroup>
                                </FormGroupColumn>
                            </GroupedRow>
                        </>
                    )}

                    <FlexCenterRow className="mb-3">
                        <Button
                            size="sm"
                            type="submit"
                            color="primary"
                            name="chargeTypeForm"
                        >
                            <FontAwesomeIcon className="mr-2" icon={faSave} />
                            {newChargeType ? 'Save New Charge Type' : 'Save'}
                        </Button>
                    </FlexCenterRow>
                </AppPageForm>
                <ChargeTypeConfigurationForm
                    ref={this.exceptionFormRef}
                    id="chargeTypeConfigForm"
                    requireIncrement
                    entity={this.state.selectedException}
                    entityName="selectedException"
                    customers={this.state.customers}
                    increments={increments}
                    chargeUnitId={chargeType.unitsId}
                    show={this.state.showExceptionForm}
                    onClose={this.onExceptionFormClosed}
                    onSaveCallback={this.onExceptionSaved}
                    onChange={this.handlers.change}
                    onSelectChange={this.handlers.select}
                    onCanBreakChanged={this.handlers.selectedException.canBreak}
                    onDoesNotApplyChanged={this.handlers.selectedException.DNA}
                    tenantSettings={tenantSettings}
                    hasHourlyEquipment={hasHourlyEquipment}
                />
            </>
        );
    }

    render() {
        if (this.state.loading) return <LinearProgress variant="indeterminate" color="secondary" />;
        return this.renderForm();
    }
}
export default withRouter(ChargeTypeForm);
